<script>
    import { onMount, afterUpdate } from 'svelte';
    import { formatFinancialNumber } from './formatFinancialNumber.js';
    import Chart from 'chart.js/auto';

    export let listing;
    let chart;
    let hasProjectedData = false;

    function createChart(canvasId, datasets) {
        const ctx = document.getElementById(canvasId)?.getContext('2d');
        if (!ctx) return;

        // Destroy existing chart if it exists
        if (chart) {
            chart.destroy();
        }

        const currentYear = new Date().getFullYear();
        const lastFinishedYear = currentYear - 1;
        const hasProjectedData = datasets.some(dataset => 
            Object.keys(dataset.data).some(year => parseInt(year) > lastFinishedYear)
        );

        const covidYears = [2020, 2021];

        chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: Object.keys(datasets[0].data).map(year => {
                    const yearNum = parseInt(year);
                    let label = year;
                    if (covidYears.includes(yearNum)) {
                        label += '*';
                    }
                    if (yearNum >= currentYear) {
                        label += '†';
                    }
                    return label;
                }),
                datasets: datasets.map(dataset => ({
                    label: dataset.label,
                    data: Object.values(dataset.data),
                    borderColor: dataset.color,
                    tension: 0.1,
                    pointStyle: Object.keys(dataset.data).map(year => parseInt(year) >= currentYear ? 'circle' : 'false'),
                    pointRadius: Object.keys(dataset.data).map(year => parseInt(year) >= currentYear ? 3 : 0),
                    segment: {
                        borderDash: context => {
                            const index = context.p1DataIndex;
                            const year = parseInt(Object.keys(dataset.data)[index]);
                            return year >= currentYear ? [5, 5] : undefined;
                        },
                    }
                }))
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                aspectRatio: 1.5,
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: function(value) {
                                return formatFinancialNumber(value, listing.currency);
                            }
                        }
                    }
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                },
                plugins: {
                    tooltip: {
                        mode: 'nearest',
                        intersect: false,
                        callbacks: {
                            footer: function(tooltipItems) {
                                const year = parseInt(tooltipItems[0].label);
                                let footer = [];
                                if (covidYears.includes(year)) {
                                    footer.push('* Data may be affected by the COVID-19 pandemic.');
                                }
                                if (year >= currentYear) {
                                    footer.push('† Projected data');
                                }
                                return footer.join('\n');
                            }
                        }
                    },
                    legend: {
                        labels: {
                            generateLabels: (chart) => {
                                const labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                                labels.forEach(label => {
                                    label.lineDash = [5, 5];
                                });
                                return labels;
                            }
                        }
                    }
                }
            }
        });

        return hasProjectedData;
    }

    $: if (listing) {
        afterUpdate(() => {
            const datasets = [];
            if (typeof listing.revenue === 'object') {
                datasets.push({ label: 'Revenue', data: listing.revenue, color: 'rgb(75, 192, 192)' });
            }
            if (typeof listing.ebitda === 'object') {
                datasets.push({ label: 'EBITDA', data: listing.ebitda, color: 'rgb(255, 99, 132)' });
            }
            if (typeof listing.grossSales === 'object') {
                datasets.push({ label: 'Gross Sales', data: listing.grossSales, color: 'rgb(54, 162, 235)' });
            }
            if (typeof listing.sellersDiscretionaryEarnings === 'object') {
                datasets.push({ label: "Seller's Discretionary Earnings", data: listing.sellersDiscretionaryEarnings, color: 'rgb(255, 206, 86)' });
            }
            if (datasets.length > 0) {
                hasProjectedData = createChart('combinedChart', datasets);
            }
        });
    }
</script>

<div class="w-full mx-auto" style="max-width: 600px;">
    <div class="relative h-[30vh]">
        <canvas id="combinedChart" class="absolute inset-0"></canvas>
    </div>
</div>
<div class="mt-2 space-y-1 text-sm text-gray-500">
    <p>* Financial reports may have been affected by the COVID-19 pandemic.</p>
    {#if hasProjectedData}
        <p>† Data for the current year and beyond is projected.</p>
    {/if}
</div> 